/**
 * PayPeriodSnapshot — Printable pay period summary
 *
 * Shows current pay period dates, financial summary (checking, upcoming bills,
 * spendable, daily allowance), bill status table, and transactions within
 * the pay period date range.
 *
 * Phase 9A
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserConfig, getActiveBills, getTransactionsByMonth,
  getPrimaryCheckingBalance,
} from '../../services/db';
import { calculateSpendable } from '../../utils/spendable';
import { getCurrentPayPeriod, getDaysUntilPayday } from '../../utils/dates';
import { formatCurrency, formatDate, getMonthKey } from '../../utils/format';
import theme from '../../utils/theme';

export default function PayPeriodSnapshot() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [config, bills, checking] = await Promise.all([
        getUserConfig(user.id),
        getActiveBills(user.id),
        getPrimaryCheckingBalance(user.id),
      ]);

      const paydayAnchor = config?.payday_anchor || '2026-02-25';
      const period = getCurrentPayPeriod(paydayAnchor);

      // Load transactions for months that overlap with the pay period
      const startMonth = getMonthKey(period.start);
      const endMonth = getMonthKey(new Date(period.end.getTime() - 86400000)); // end is exclusive
      const monthKeys = [startMonth];
      if (endMonth !== startMonth) monthKeys.push(endMonth);

      const txnArrays = await Promise.all(
        monthKeys.map(mk => getTransactionsByMonth(user.id, mk))
      );
      const allTxns = txnArrays.flat();

      // Filter transactions within pay period [start, end)
      const periodTxns = allTxns.filter(txn => {
        const txnDate = new Date(txn.date + 'T00:00:00');
        return txnDate >= period.start && txnDate < period.end;
      }).sort((a, b) => new Date(b.date) - new Date(a.date));

      const spendable = calculateSpendable({
        checkingBalance: checking,
        bills,
        paydayAnchor,
      });

      setData({
        period,
        bills,
        periodTxns,
        spendable,
        checking,
        daysUntilPayday: getDaysUntilPayday(paydayAnchor),
      });
    } catch (err) {
      console.error('Failed to load pay period report:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div className="report-content"><p className="empty-state">Loading report...</p></div>;
  }

  if (!data) {
    return <div className="report-content"><p className="empty-state">Unable to load report data.</p></div>;
  }

  const { period, bills, periodTxns, spendable, checking, daysUntilPayday } = data;
  const periodLabel = `${formatDate(period.start)} to ${formatDate(new Date(period.end.getTime() - 86400000))}`;

  // Bills due within this pay period
  const periodBills = bills.map(bill => {
    const dueDate = new Date(period.start.getFullYear(), period.start.getMonth(), bill.due_day);
    // Check if due date falls in period
    let inPeriod = dueDate >= period.start && dueDate < period.end;
    if (!inPeriod) {
      // Try next month
      const nextMonthDue = new Date(period.start.getFullYear(), period.start.getMonth() + 1, bill.due_day);
      inPeriod = nextMonthDue >= period.start && nextMonthDue < period.end;
    }
    return { ...bill, inPeriod };
  }).filter(b => b.inPeriod);

  const upcomingTotal = periodBills.filter(b => !b.current_cycle_paid).reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div className="report-content">
      {/* Print-only header */}
      <div className="report-header">
        <div className="report-header-title">Spendable — Pay Period</div>
        <div className="report-header-subtitle">{periodLabel}</div>
      </div>

      {/* Period dates */}
      <div className="report-section">
        <div style={{ textAlign: 'center', fontSize: '1rem', fontWeight: 700, color: theme.text, marginBottom: '0.75rem' }}>
          {periodLabel}
        </div>
      </div>

      {/* Financial summary */}
      <div className="report-section">
        <div className="report-section-title">Financial Summary</div>
        <div className="report-summary-row">
          <span className="report-summary-label">Checking Balance</span>
          <span className="report-summary-value">{formatCurrency(checking)}</span>
        </div>
        <div className="report-summary-row">
          <span className="report-summary-label">Upcoming Bills</span>
          <span className="report-summary-value" style={{ color: theme.danger }}>
            {formatCurrency(upcomingTotal)}
          </span>
        </div>
        <div className="report-summary-row">
          <span className="report-summary-label">Spendable</span>
          <span className="report-summary-value" style={{ color: spendable.isNegative ? theme.danger : theme.success }}>
            {formatCurrency(spendable.spendable)}
          </span>
        </div>
        <div className="report-summary-row">
          <span className="report-summary-label">Daily Allowance</span>
          <span className="report-summary-value">
            {formatCurrency(spendable.dailyAllowance, false)}/day ({daysUntilPayday} days)
          </span>
        </div>
      </div>

      {/* Bills in this period */}
      <div className="report-section">
        <div className="report-section-title">Bills This Period</div>
        {periodBills.length === 0 ? (
          <p className="empty-state">No bills due this period</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Due</th>
                <th className="text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {periodBills.map(bill => (
                <tr key={bill.id}>
                  <td>{bill.name}</td>
                  <td className="text-right">{formatCurrency(bill.amount)}</td>
                  <td className="text-right">{bill.due_day}</td>
                  <td className="text-right" style={{ color: bill.current_cycle_paid ? theme.success : theme.textMuted }}>
                    {bill.current_cycle_paid ? '\u2713 Paid' : '\u2014 Unpaid'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Transactions in period */}
      <div className="report-section">
        <div className="report-section-title">
          Transactions ({periodTxns.length})
        </div>
        {periodTxns.length === 0 ? (
          <p className="empty-state">No transactions this period</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {periodTxns.map(txn => (
                <tr key={txn.id}>
                  <td>{formatDate(txn.date)}</td>
                  <td style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {txn.description}
                  </td>
                  <td>{txn.category || '\u2014'}</td>
                  <td className="text-right" style={{ fontWeight: 600 }}>
                    {formatCurrency(txn.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="report-footer">
        Generated {formatDate(new Date(), 'full')}
      </div>
    </div>
  );
}
